/**
 * operator-client.ts — F-007 Phase 4 (IMPL-P4-003).
 *
 * Typed fetch wrappers per /api/v1/operator/*. Mirror della convenzione di
 * `features/pratica/lib/pratica-client.ts`:
 *   - `credentials: 'include'` su ogni richiesta (cookie sessione Better Auth)
 *   - errori non-2xx propagati come `Error` con `status` + `body` per consentire
 *     ai TanStack Query mutation hooks di branchare sul codice HTTP (es. 409).
 *
 * Endpoint coperti (cfr. INTRODUCTION.md §"High-Level Architecture"):
 *   GET    /api/v1/operator/queue              → listQueue
 *   GET    /api/v1/operator/my-assigned        → listMyAssigned
 *   GET    /api/v1/operator/:id                → getDetail
 *   POST   /api/v1/operator/:id/take           → take (409 in caso di race)
 *   POST   /api/v1/operator/:id/release        → release
 *   POST   /api/v1/operator/:id/segnala-errore → segnalaErrore
 *   GET    /api/v1/operator/:id/pdf-url        → getPdfUrl (TTL 60s)
 */
import type {
  OperatorQueueResponse,
  OperatorPraticaDetailDTO,
  TakeResponse,
  ReleaseResponse,
  BlockedResponse,
  PdfUrlResponse,
  QueueFilters,
  SegnalazioneInput,
  PresignRicevutaInput,
  CommitRicevutaInput,
} from '@app/shared';

const BASE = '/api/v1/operator';

/**
 * Errore lanciato da `apiFetch` su risposta non-2xx. Espone `status` e il
 * `body` JSON parsato (oggetto vuoto in caso di body non-JSON) per consentire
 * ai mutation hook di leggere campi tipo `assignedOperatorName` su 409.
 */
export interface OperatorApiError extends Error {
  status: number;
  body: unknown;
}

export function isOperatorApiError(err: unknown): err is OperatorApiError {
  return (
    err instanceof Error &&
    typeof (err as OperatorApiError).status === 'number' &&
    'body' in err
  );
}

async function apiFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: 'include', ...init });
  if (!res.ok) {
    const body: unknown = await res.json().catch(() => ({}));
    const code =
      body && typeof body === 'object' && 'code' in body
        ? String((body as { code?: unknown }).code ?? res.statusText)
        : res.statusText;
    const err = Object.assign(new Error(code), {
      status: res.status,
      body,
    }) as OperatorApiError;
    throw err;
  }
  // Some endpoints may return empty body — guard.
  const text = await res.text();
  if (!text) return {} as T;
  return JSON.parse(text) as T;
}

/**
 * Filtri accettati dalla GET /queue. Tipo opzionale e parziale: il backend
 * applica i default (page=1, pageSize=20) se mancano.
 */
export type QueueFiltersInput = Partial<QueueFilters>;

export const operatorClient = {
  listQueue: (filters: QueueFiltersInput = {}) => {
    const entries = Object.entries(filters)
      .filter(([, v]) => v !== undefined && v !== null)
      .map(([k, v]) => [k, String(v)] as [string, string]);
    const qs = new URLSearchParams(entries).toString();
    const suffix = qs ? `?${qs}` : '';
    return apiFetch<OperatorQueueResponse>(`${BASE}/queue${suffix}`);
  },

  listMyAssigned: () =>
    apiFetch<OperatorPraticaDetailDTO[]>(`${BASE}/my-assigned`),

  getDetail: (id: string) =>
    apiFetch<OperatorPraticaDetailDTO>(`${BASE}/${id}`),

  take: (id: string) =>
    apiFetch<TakeResponse>(`${BASE}/${id}/take`, { method: 'POST' }),

  release: (id: string) =>
    apiFetch<ReleaseResponse>(`${BASE}/${id}/release`, { method: 'POST' }),

  segnalaErrore: (id: string, input: SegnalazioneInput) =>
    apiFetch<BlockedResponse>(`${BASE}/${id}/segnala-errore`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),

  getPdfUrl: (id: string) =>
    apiFetch<PdfUrlResponse>(`${BASE}/${id}/pdf-url`),

  // -------------------------------------------------------------------------
  // F-008 Phase 5 — Ricevuta upload (two-phase: presign + commit)
  // -------------------------------------------------------------------------
  /**
   * POST /api/v1/operator/ricevuta/presign — chiede al backend una presigned
   * PUT URL su IONOS S3 (TTL 600s) per caricare la ricevuta AdE firmata.
   * La `s3Key` è calcolata server-side
   * (`accounts/{pratica.accountId}/ricevute/{uuid}.pdf`) — il client NON la
   * fornisce (security rule #15: accountId mai dal request body).
   *
   * Errors propagati come `OperatorApiError`:
   *  - 400 `OPERATOR_RICEVUTA_INPUT_ERROR` — mime/size invalido
   *  - 401 `UNAUTHORIZED` — sessione assente
   *  - 403 `FORBIDDEN` — pratica non assegnata all'operatore
   *  - 422 `OPERATOR_RICEVUTA_STATE_ERROR` — stato pratica ∉ {IN_ELABORAZIONE, IN_CARICO}
   *  - 500 `OPERATOR_RICEVUTA_STORAGE_ERROR` — S3 unreachable
   */
  presignRicevutaUpload: (input: PresignRicevutaInput) =>
    apiFetch<PresignRicevutaResponse>(`${BASE}/ricevuta/presign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),

  /**
   * POST /api/v1/operator/ricevuta/commit — conferma upload S3 + transizione
   * atomica della pratica a `COMPLETATA`. Il backend esegue HEAD su `s3Key`,
   * inserisce timeline events `RICEVUTA_UPLOADED` + `COMPLETATA` e scrive
   * audit log dentro la stessa `$transaction(ReadCommitted)` (GDPR D-004).
   *
   * Errors propagati come `OperatorApiError`:
   *  - 400 `VALIDATION_ERROR` — Zod superRefine (firma UNRECOGNIZED senza
   *    note ≥10 char, oppure dataRegistrazione futura)
   *  - 400 `OPERATOR_RICEVUTA_INPUT_ERROR` — s3Key shape invalido
   *  - 409 `OPERATOR_PROTOCOLLO_ADE_DUPLICATE` — protocollo AdE già usato
   *  - 422 `OPERATOR_RICEVUTA_STATE_ERROR` — stato pratica cambiato (con
   *    `currentStato` nel body)
   *  - 500 `OPERATOR_RICEVUTA_STORAGE_ERROR` — HEAD su S3 fallita
   */
  commitRicevuta: (input: CommitRicevutaInput) =>
    apiFetch<CommitRicevutaResponse>(`${BASE}/ricevuta/commit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }),

  /**
   * GET /api/v1/operator/:id/ricevuta-pdf-url — presigned GET URL della
   * ricevuta caricata (TTL 60s) per render in iframe lato operatore.
   * Mirror di `getPdfUrl` ma per la ricevuta anziché per il contratto
   * originale.
   */
  getRicevutaPdfUrl: (id: string) =>
    apiFetch<PdfUrlResponse>(`${BASE}/${id}/ricevuta-pdf-url`),
};

export type OperatorClient = typeof operatorClient;

/* -------------------------------------------------------------------------- */
/* F-008 Phase 5 — Ricevuta response types                                    */
/* -------------------------------------------------------------------------- */

/**
 * Response del POST /ricevuta/presign — presigned PUT URL verso IONOS S3.
 *  - `uploadUrl`: URL firmata (header `Content-Type: application/pdf` richiesto)
 *  - `s3Key`    : chiave canonica `accounts/{accountId}/ricevute/{uuid}.pdf`
 *  - `expiresAt`: ISO timestamp (Date serializzata) — il client deve
 *                 completare la PUT prima della scadenza (TTL 600s)
 */
export interface PresignRicevutaResponse {
  uploadUrl: string;
  s3Key: string;
  expiresAt: string;
}

/**
 * Response del POST /ricevuta/commit — DTO PII-safe della pratica completata
 * (vedi `apps/api/src/routes/operator/ricevuta.ts` per il filtering).
 *
 * Nota: i campi sono dichiarati nullable perché il backend li restituisce
 * `null` quando il commit fallisce in modalità degraded — ma in path felice
 * sono sempre popolati. Il caller deve assumere che siano valorizzati su
 * 200 OK (in caso contrario è un bug backend).
 */
export interface CommitRicevutaResponse {
  id: string;
  stato: string;
  protocolloAde: string | null;
  dataRegistrazione: string | null;
  ricevutaUploadedAt: string | null;
  firmaDigitaleStatus: string | null;
}
